import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as sliceGrouper from '@amcharts/amcharts4/plugins/sliceGrouper';
import { Amcharts } from './Amcharts';

type PieLabelPosition = 'outside' | 'surface' | 'none' | 'default';
export class AmchartsPiechart extends Amcharts {

    constructor() {
        super();
    }

    createByRef(ref: any, context: any): any {
        //context.settings = this.getSettings(context);
        this.create(ref, am4charts.PieChart, context);

        this.applyTheme(context);
        this.chart.innerRadius = am4core.percent(context.settings.pieRadius || 0);
        this.chart.radius = am4core.percent(context.settings.radius || 80);
        for (let i = 0; i < context.measures.length; i++) {
            let measure = context.measures[i];
            for (let j = 0; j < context.dimensions.length; j++) {
                let dimension = context.dimensions[j];
                let measureResultName = measure.resultName || "value" + i;
                let dimensionResultName = dimension.resultName || "key" + j;

                let series = this.createSeries(measureResultName, dimensionResultName, context);
                this.setSeriesSettings(series, context, measureResultName);
                this.onClickSliceHandler(this.chart, series, context);
            }
        }

        return this;
    }

    onClickSliceHandler(_chart: any, series: any, context: any) {
        if (context.disableInteraction)
            return;

        if (series.slices && context.onClickColumn) {
            const me = this;
            const jsonProps = context.jsonProperties || { resultName: 'resultName', values: 'values', active: 'active', compacted: 'compacted' };

            series.slices.template.events.on("hit", function (ev: any) {
                if (ev.target.dataItem.dataContext.sliceGrouperOther)
                    return;

                let activeItems: any[] = [];
                let series = ev.target.dataItem.component;
                series.slices.each((item: any) => {
                    if (!item.isActive)
                        return;

                    let clickContext = JSON.parse(JSON.stringify(item.dataItem.dataContext));
                    clickContext[jsonProps.active] = item.isActive;

                    let resultName = context.dimensions[0].resultName;
                    if (resultName) {
                        let keyValue = clickContext[resultName];

                        if (keyValue === context.settings.sliceGroupTitle) {
                            let filtered = me.chart.data
                                .filter((d: any) => d[resultName] !== keyValue)
                                .map((d: any) => ({ [resultName]: d[resultName] }));

                            let compactor = {
                                [jsonProps.resultName]: resultName,
                                [jsonProps.values]: filtered,
                                [jsonProps.active]: ev.target.isActive,
                                [jsonProps.compacted]: true
                            }

                            let compactorContext = JSON.parse(JSON.stringify(compactor));
                            activeItems.push(compactorContext);
                        }
                        else {
                            clickContext[jsonProps.compacted] = false;
                            activeItems.push(clickContext);
                        }
                    }
                });

                return context.onClickColumn.callee.invokeMethodAsync(context.onClickColumn.function, activeItems);
            }, this);
        }
    }

    createSerieses(measures: any, categoryFieldName: any, _context: any) {
        let i = this.chart.series.length;
        let me = this;
        measures.forEach(function (measure: { resultName: string; }) {
            let valueFieldName = measure.resultName || "value" + i;
            me.createSeries(valueFieldName, categoryFieldName, _context);
            i++;
        });
    }

    createSeries(valueFieldName: any, categoryFieldName: any, context: any) {
        let series = this.chart.series.push(new am4charts.PieSeries());

        series.dataFields.value = valueFieldName;
        series.dataFields.category = categoryFieldName;
        series.slices.template.adapter.add("fill", (fill: any, target: any) =>
            target.dataItem ? this.getThemeColors(context)[target.dataItem.index] : fill
        );
        series.slices.template.stroke = am4core.color("white");
        series.slices.template.strokeWidth = 2;
        series.slices.template.strokeOpacity = 1;
        series.slices.template.cornerRadius = 2;
        series.slices.template.tooltipText = this.context.settings.disableTooltips  ? "" : "{category}: [bold]{value}[/]";
        
        //series.slices.template.tooltipText = "{category}: {value.percent.formatNumber('#,###.0')}% ({value})";

        let pieRadius = this.context.settings.radius || 80;
        let labelMaxWidth = this.context.settings.pieLabelPosition == 'surface' && pieRadius >= 80 ? 100 : undefined;
        series.labels.template.maxWidth = this.context.settings.labelMaxWidth || labelMaxWidth || 250;
        series.labels.template.text = this.getPieLabelText(this.context.settings);

        series.legendSettings.valueText = "{value.percent.formatNumber()}%";
        series.labels.template.truncate = false;
        series.labels.template.wrap = true;

        return series;
    }

    setSeriesSettings(series: any, context: any, valueFieldName: any) {

        let settings = this.getSettings(context);
        const measureAppearance = settings.measures.find((f: any) => f.name == valueFieldName);
        let prefix = this.getPrefix(context, measureAppearance, settings);
        let suffix = this.getSuffix(context, measureAppearance, settings);
        series.slices.template.tooltipText = this.context.settings.disableTooltips ? "" : `{category}: [bold]${prefix}{value}${suffix}[/]`;

        this.setSeriesSettingsByPieLabelPosition(series, settings.pieLabelPosition)

        series.labels.template.fill = am4core.color(this.getTextPrimaryColor() || settings.pieLabelColor);
        series.labels.template.fontSize = settings.pieLabelFontSize + 'px';
        series.labels.template.fontWeight = settings.pieLabelFontWeight;
        series.labels.template.paddingTop = 0;
        series.labels.template.paddingBottom = 0;
        series.labels.template.text = this.getPieLabelText(this.context.settings);

        if (settings.enableSliceGroup) {

            if (series.plugins.values.length == 0)
                series.plugins.push(new sliceGrouper.SliceGrouper());

            series.plugins.values[0].threshold = settings.sliceGroupValue;
            series.plugins.values[0].groupName = settings.sliceGroupTitle;
            series.plugins.values[0].clickBehavior = settings.sliceGroupBehavior;
        }

        if (settings.includeAllRows) {
            const plugin = series.plugins.values.find((p: any) => p instanceof sliceGrouper.SliceGrouper);
            if (plugin) {
                series.plugins.removeValue(plugin);
            }
        }
    }

    setSeriesSettingsByPieLabelPosition(series: any, pieLabelPosition: PieLabelPosition) {
        const config: any = {
            outside: { ticksDisabled: false, labelsDisabled: false, labelRadius: 5 },
            surface: { ticksDisabled: false, labelsDisabled: false, alignLabels: false },
            none: { ticksDisabled: true, labelsDisabled: true },
            default: { ticksDisabled: false, labelsDisabled: false, labelRadius: -30 }
        };

        const mode = config[pieLabelPosition] || config.default;

        series.ticks.template.disabled = mode.ticksDisabled ?? false;
        series.labels.template.disabled = mode.labelsDisabled ?? false;

        if (mode.labelRadius !== undefined)
            series.labels.template.radius = am4core.percent(mode.labelRadius);

        if (mode.alignLabels !== undefined)
            series.alignLabels = mode.alignLabels;
    }

    removeSeries(index: any) {
        this.chart.series.removeIndex(index);
    }

    removeSerieses() {
        while (this.chart.series.length) {
            this.chart.series.removeIndex(0);
        }
    }

    createLegend(): any {
        let legend = super.createLegend();

        legend.itemContainers.template.events.on("hit", (ev: any) => {
            let slice = ev.target.dataItem.dataContext.slice;
            slice.isActive = !slice.isActive;
        });
        return legend;
    }

    updateSettings(context: any) {
        super.updateSettings(context);

        let settings = this.getSettings(context);

        if (settings) {
            this.chart.innerRadius = am4core.percent(settings.pieRadius || 0);
            this.chart.series.each((s: any) => this.setSeriesSettings(s, context, s.dataFields.value));
            if (this.chart.data && this.chart.data.length > 0)
                this.setLegend(context);            
        }
    }

    setData(context: any) {
        if (context.data) {            
            this.chart.data = context.data;
            this.setLegend(context);            
        }
        else
            this.clearData();
    }

    markActiveByValue(values: any[]) {
        setTimeout(() => {
            this.chart.series.each((s: any) => s.slices.each((ss: any, _i: number) => {
                values.forEach((v: any) => {
                    if (v == ss.dataItem.category) {
                        ss.isActive = true;
                    }
                })
            }));
        }, 100);
    }

    getSettings(context: any) {
        context.settings.pieRadius = context.settings.pieRadius ?? context.settings.donutRadius;
        context.settings.pieLabelFontSize = !context.settings.pieLabelFontSize ? context.settings.labelFontSize : context.settings.pieLabelFontSize;
        context.settings.pieLabelFontWeight = !context.settings.pieLabelFontWeight ? context.settings.labelFontWeight : context.settings.pieLabelFontWeight;
        context.settings.pieLabelColor = !context.settings.pieLabelColor ? context.settings.labelColor : context.settings.pieLabelColor;
        context.settings.pieLabelPosition = !context.settings.pieLabelPosition ? context.settings.labelPosition : context.settings.pieLabelPosition;

        return context.settings;
    }

    getPieLabelText(settings: any): string {
        switch (settings.labelDisplay) {
            case "percent":
                return "{value.percent.formatNumber()}%";

            case "full":
            default:
                return "{category}: {value.percent.formatNumber()}%";
        }
    }
}